import React, { createContext, useContext, ReactNode, useState, useMemo } from 'react';
import { ShortlistState, ShortlistActions, ShortlistProfile } from '../features/shortlist/types';
import { ContactFormData } from '../features/shortlist/components/ContactForm';

interface ShortlistContextType extends ShortlistState, ShortlistActions {
  addProfileToShortlist: (profile: ShortlistProfile) => void;
  removeProfileFromShortlist: (profileId: string) => void;
  getShortlistedProfiles: () => ShortlistProfile[];
}

const ShortlistContext = createContext<ShortlistContextType | undefined>(undefined);

interface ShortlistProviderProps {
  children: ReactNode;
}

export const ShortlistProvider: React.FC<ShortlistProviderProps> = ({ children }) => {
  const [shortlistedProfiles, setShortlistedProfiles] = useState<Set<string>>(new Set());
  const [shortlistProfiles, setShortlistProfiles] = useState<Map<string, ShortlistProfile>>(new Map());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addToShortlist = (profileId: string) => {
    setShortlistedProfiles(prev => new Set(Array.from(prev).concat(profileId)));
  };

  const removeFromShortlist = (profileId: string) => {
    setShortlistedProfiles(prev => {
      const newSet = new Set(prev);
      newSet.delete(profileId);
      return newSet;
    });
    setShortlistProfiles(prev => {
      const newMap = new Map(prev);
      newMap.delete(profileId);
      return newMap;
    });
  };

  const addProfileToShortlist = (profile: ShortlistProfile) => {
    setShortlistedProfiles(prev => new Set(Array.from(prev).concat(profile.id)));
    setShortlistProfiles(prev => new Map(prev).set(profile.id, profile));
  };

  const removeProfileFromShortlist = (profileId: string) => {
    setShortlistedProfiles(prev => {
      const newSet = new Set(prev);
      newSet.delete(profileId);
      return newSet;
    });
    setShortlistProfiles(prev => {
      const newMap = new Map(prev);
      newMap.delete(profileId);
      return newMap;
    });
  };

  const getShortlistedProfiles = () => {
    return Array.from(shortlistProfiles.values());
  };

  const clearShortlist = () => {
    setShortlistedProfiles(new Set());
    setShortlistProfiles(new Map());
  };

  const isInShortlist = (profileId: string) => {
    return shortlistedProfiles.has(profileId);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const submitQuoteRequest = async (formData: ContactFormData): Promise<string> => {
    setIsSubmitting(true);
    try {
      const shortlistIds = Array.from(shortlistedProfiles);
      const requestData = {
        ...formData,
        shortlistIds,
      };

      const response = await fetch('/api/shortlists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote request');
      }

      const result = await response.json();

      // Clear shortlist and close modal on success
      clearShortlist();
      closeModal();

      // Return the shortlist ID for redirection
      return result.shortlistId;
    } catch (error) {
      console.error('Error submitting quote request:', error);
      throw error; // Re-throw to let the form handle the error
    } finally {
      setIsSubmitting(false);
    }
  };

  const shortlistItems = useMemo(() => getShortlistedProfiles(), [shortlistProfiles]);

  const contextValue: ShortlistContextType = {
    shortlistedProfiles,
    shortlistItems,
    isModalOpen,
    isSubmitting,
    addToShortlist,
    removeFromShortlist,
    addProfileToShortlist,
    removeProfileFromShortlist,
    getShortlistedProfiles,
    clearShortlist,
    isInShortlist,
    openModal,
    closeModal,
    submitQuoteRequest,
  };

  return (
    <ShortlistContext.Provider value={contextValue}>
      {children}
    </ShortlistContext.Provider>
  );
};

export const useShortlistContext = (): ShortlistContextType => {
  const context = useContext(ShortlistContext);
  if (context === undefined) {
    throw new Error('useShortlistContext must be used within a ShortlistProvider');
  }
  return context;
};
